import { useEffect, useState } from "react";

export interface Resource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface ResourceOptions {
  /**
   * When `false`, the loader is not run and the resource is reset to its
   * idle, data-less, not-loading state. Preserves the page effects'
   * `if (!tenant) return;` guards without leaking that condition into the
   * loader.
   */
  enabled?: boolean;
}

/**
 * Generic async-resource loader for admin data pages.
 *
 * Owns the one invariant that kept drifting between hand-written page effects:
 * a StrictMode/unmount `active` guard so a resolution that lands after the
 * component is gone (or after a doubled effect run) never sets state.
 *
 * The hook takes a `loader` thunk and never inspects auth `creds`. That is the
 * encoded auth-lane rule (ADR-0001): both lanes fetch the same way — Basic
 * (sessionStorage creds) and Google cookie (`creds === null`, the api layer
 * rides the HttpOnly cookie via `credentials: "include"`). Gating a fetch on
 * `creds` truthiness would silently break the cookie lane, so this seam simply
 * has no `creds` to gate on.
 *
 * `deps` drive re-runs exactly like a `useEffect` dependency list.
 */
export function useResource<T>(
  loader: () => Promise<T>,
  deps: unknown[],
  options: ResourceOptions = {},
): Resource<T> {
  const { enabled = true } = options;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true; // guard against StrictMode double-invoke / unmount
    setData(null);
    setError(null);
    if (!enabled) {
      setLoading(false); // disabled means idle, not stuck loading
      return; // gate stays here, never on creds
    }
    setLoading(true);
    loader()
      .then((value) => {
        if (active) {
          setData(value);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError("Could not load.");
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { data, loading, error };
}
